import { useTranslation } from "react-i18next";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { TESTIMONIALS, type Testimonial } from "@/data/testimonials";

type Props = {
  /** Limit to one service's testimonials; all when omitted. */
  service?: Testimonial["service"];
};

export function TestimonialsSection({ service }: Props) {
  const { i18n } = useTranslation();
  const en = i18n.language === "en";
  const items = service ? TESTIMONIALS.filter((item) => item.service === service) : TESTIMONIALS;
  if (items.length === 0) return null;

  return (
    <section aria-labelledby="testimonials-heading" className="bg-white">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
        <SectionHeader
          id="testimonials-heading"
          title={en ? "What clients say" : "Lo que dicen mis clientes"}
          align={items.length === 1 ? "center" : "left"}
        />
        <ul className={`mt-14 grid gap-6 ${items.length === 1 ? "mx-auto max-w-3xl" : "md:grid-cols-2 lg:grid-cols-3"}`}>
          {items.map((item, i) => (
            <li
              key={item.name}
              data-reveal=""
              style={{ ["--reveal-delay" as string]: `${i * 80}ms` }}
              className={`flex flex-col rounded-[1.75rem] p-8 ${items.length === 1 ? "sm:p-12" : ""} ${i % 3 === 1 ? "bg-brand-primary text-white" : "border border-brand-primary/10 bg-brand-bg"}`}
            >
              <figure className="flex h-full flex-col">
                <blockquote className="font-display text-xl leading-snug">“{en ? item.quote.en : item.quote.es}”</blockquote>
                <figcaption className="mt-auto flex items-center gap-3 pt-8">
                  {item.photo ? (
                    <img src={item.photo} alt="" width={48} height={48} loading="lazy" className="size-12 rounded-full object-cover" />
                  ) : null}
                  <span>
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block font-semibold underline decoration-current/30 underline-offset-4 transition-colors hover:decoration-current"
                      >
                        {item.name}
                        <span className="sr-only"> {en ? "(opens in a new tab)" : "(abre en nueva pestaña)"}</span>
                      </a>
                    ) : (
                      <span className="block font-semibold">{item.name}</span>
                    )}
                    <span className={`block text-sm ${i % 3 === 1 ? "text-on-navy-muted" : "text-ink-muted"}`}>
                      {en ? item.business.en : item.business.es}
                    </span>
                  </span>
                </figcaption>
              </figure>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
